import React, { useEffect, useState } from "react";
import { getCrmAiProvidersApi } from "./ai-chat-api.js";

const PROVIDER_LABELS = { claude: "Claude", openai: "OpenAI", gemini: "Gemini" };

// Score, rationale and next action for one record. Shared by every module
// that exposes an ai_summary endpoint; the backend persists the result, so
// `initial` is whatever the view page already loaded.
// `module` is the key the admin AI Configuration page stores this card's
// provider and model under ("lead", "account", "opportunity", "quotation").
// Without it the card opens on the chatbot's provider, which is a different
// setting that happens to live next to it.
const AiSummaryCard = ({ api, id, initial, module, title = "AI Summary", hint }) => {
  const [fresh, setFresh] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  // Provider and model flattened into one value ("claude|claude-opus-5"),
  // because a card this small does not have room for two controls and the
  // pair is only ever chosen together.
  const [choices, setChoices] = useState([]);
  const [choice, setChoice] = useState("");
  // True when the admin page fixed this module's model, so the card shows it
  // instead of a dropdown.
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      const response = await getCrmAiProvidersApi();
      if (!live || response.data?.success !== true) return;

      const flat = [];
      (response.data.providers ?? []).forEach((p) =>
        (p.models ?? []).forEach((m) => flat.push(`${p.provider}|${m}`)),
      );

      // What the admin configured for THIS module wins. The chatbot's
      // provider is only the fallback - it is a separate setting, and using
      // it here made every card look configured when none of them were.
      const forModule = module ? response.data.summaries?.[module] : null;
      const preferred = (response.data.providers ?? []).find(
        (p) => p.provider === response.data.default,
      );

      const wanted = forModule?.provider
        ? `${forModule.provider}|${forModule.model}`
        : preferred
          ? `${preferred.provider}|${response.data.default_model || preferred.model}`
          : "";

      // A module configured in the admin page is not the rep's to change:
      // that page is where the model for each module is decided. The card
      // shows what will run rather than offering a picker. Only an
      // unconfigured module falls back to the chatbot default and lets the
      // rep choose.
      if (forModule?.provider) {
        setLocked(true);
        setChoices([wanted]);
      } else {
        setChoices(flat);
      }

      setChoice(wanted || flat[0] || "");
    })();
    return () => {
      live = false;
    };
  }, [module]);

  // The record carries ai_score/ai_rationale/ai_next_step; a freshly
  // generated result comes back as score/rationale/next_step. Normalise the
  // stored shape so the card only ever deals with one.
  const stored =
    initial?.ai_score !== null && initial?.ai_score !== undefined
      ? {
          score: initial.ai_score,
          rationale: initial.ai_rationale,
          next_step: initial.ai_next_step,
        }
      : null;

  // Derived, not synced: `fresh` is a result generated during this visit,
  // `stored` is what the record already carried. No effect to keep in step,
  // so no stale-state bug.
  const summary = fresh ?? stored;

  const generate = async () => {
    setLoading(true);
    setError("");
    setNote("");
    try {
      // A button labelled Regenerate that returns the stored copy is lying,
      // so an explicit click bypasses the unchanged-record short circuit.
      // The first Generate does not, and neither does a page load.
      // A locked module sends nothing: the server reads the same admin
      // configuration, and sending it from here would let a page left open
      // across a configuration change run the old model.
      const [provider, model] = choice.split("|");
      const response = await api(id, {
        ...(summary ? { force: true } : {}),
        ...(!locked && provider ? { provider, model } : {}),
      });
      if (response?.data?.success === true) {
        setFresh(response.data.data);
        // Name the model that actually ran, not the one selected - they
        // differ whenever a stored summary is served back.
        setNote(
          response.data.cached
            ? response.data.message
            : response.data.model
              ? `Generated by ${response.data.model}`
              : "",
        );
      } else {
        setError(response?.data?.message || "Could not generate summary");
      }
    } finally {
      setLoading(false);
    }
  };

  const band =
    summary?.score >= 70 ? "#1F9254" : summary?.score >= 40 ? "#C58A00" : "#CC3333";

  return (
    <div style={card}>
      <div style={head}>
        <span style={{ alignItems: "center", display: "flex", gap: 10 }}>
          {title}
          
        </span>
        <div style={{ alignItems: "center", display: "flex", gap: 10 }}>
          {summary && (
            <span style={{ color: band, fontSize: 20, fontWeight: 600 }}>
              {summary.score}
              <span style={{ color: "#7A869A", fontSize: 12 }}>/100</span>
            </span>
          )}
          {/* Both halves shown either way: a bare model name does not tell a
              rep which AI they are about to spend money with. */}
          {locked
            ? choice && (
                <span
                  style={modelFixed}
                  title="Set for this module in AI Configuration"
                >
                  {(PROVIDER_LABELS[choice.split("|")[0]] ?? choice.split("|")[0]) +
                    " · " +
                    choice.split("|")[1]}
                </span>
              )
            : choices.length > 1 && (
                <select
                  value={choice}
                  onChange={(e) => setChoice(e.target.value)}
                  disabled={loading}
                  title="Which AI generates this summary"
                  style={modelSelect}
                >
                  {choices.map((c) => {
                    const [p, m] = c.split("|");
                    return (
                      <option key={c} value={c}>
                        {(PROVIDER_LABELS[p] ?? p) + " · " + m}
                      </option>
                    );
                  })}
                </select>
              )}

        <button
          type="button"
          onClick={generate}
          disabled={loading}
          style={{ ...btn, cursor: loading ? "default" : "pointer", opacity: loading ? 0.6 : 1 }}
        >
          {loading ? "Analysing…" : summary ? "Regenerate" : "Generate"}
        </button>
        </div>
      </div>

      {loading && <div style={muted}>Reading the record…</div>}

      {!loading && error && <div style={{ ...muted, color: "#CC3333" }}>{error}</div>}

      {!loading && !error && !summary && <div style={muted}>{hint}</div>}

      {/* The score sits in the header beside the title now, so the body
          starts with the reasoning. */}
      {!loading && summary && (
        <div style={{ marginTop: 12 }}>
          <div style={{ color: "#172B4D", fontSize: 14, lineHeight: 1.6 }}>
            {summary.rationale}
          </div>
          <div style={{ color: "#172B4D", fontSize: 14, marginTop: 12 }}>
            <strong>Next action: </strong>
            {summary.next_step}
          </div>
          {note && (
            <div style={{ color: "#7A869A", fontSize: 11, marginTop: 10 }}>{note}</div>
          )}
        </div>
      )}
    </div>
  );
};

const card = {
  // Explicit white rather than inherited: the app shell's main content is
  // grey (#f8f9fb), so a transparent card reads as part of the page instead
  // of sitting on it.
  background: "#FFFFFF",
  border: "1px solid #EBEBEB",
  borderRadius: 12,
  marginTop: 16,
  padding: 16,
};

const head = {
  alignItems: "center",
  color: "#172B4D",
  display: "flex",
  fontSize: 16,
  fontWeight: 600,
  justifyContent: "space-between",
};

const btn = {
  background: "none",
  border: "1px solid #3D4AF5",
  borderRadius: 24,
  color: "#3D4AF5",
  fontSize: 14,
  fontWeight: 500,
  padding: "6px 16px",
};

const muted = {
  color: "#7A869A",
  fontSize: 14,
  marginTop: 12,
};

const modelFixed = {
  color: "#7A869A",
  fontSize: 11,
  fontWeight: 400,
  maxWidth: 200,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const modelSelect = {
  border: "1px solid #EBEBEB",
  borderRadius: 12,
  color: "#7A869A",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 400,
  maxWidth: 160,
  outline: "none",
  padding: "3px 6px",
};

export default AiSummaryCard;
